import moment from 'moment';
import { GPC } from 'api';
import { EXTERNAL_VALUATION_REFERENCE_TYPE, SSV_REFERENCE_TYPE } from 'common/constants/documents';
import { NewVersionFormState } from 'pages/Allocation/allocations/components/NewVersionForm/NewVersionForm.props';
import {
  ALLOCATION_SCENARIO_METHOD_OPM,
  ALLOCATION_SCENARIO_TYPE_CURRENT_VALUE,
} from 'pages/ValuationsAllocation/common/constants/allocation';
import getSlugValue from 'utilities/getSlugValue/getSlugValue';

export const VALUATIONS_VALUATION = 'valuation';
export const TAB_QUERY_PARAM = 'tab';

export const VALUATIONS_SPREADSHEET_COLUMN_NAME = 'approach';
export const VALUATIONS_SPREADSHEET_COLUMN_NAME_PLURAL = 'approaches';
export const VALUATIONS_SPREADSHEET_TITLE = 'Valuation';
export const VALUATIONS_SPREADSHEET_SLUG = getSlugValue(VALUATIONS_SPREADSHEET_TITLE);
export const VALUATIONS_SPREADSHEET_TABLE = `${VALUATIONS_SPREADSHEET_SLUG}-table`;
export const COMPANY_ROW_ALIAS = 'company';

export const VALUATIONS_SPREADSHEET_TABLE_TERMS = Object.freeze({
  columnName: VALUATIONS_SPREADSHEET_COLUMN_NAME,
  pluralColumnName: VALUATIONS_SPREADSHEET_COLUMN_NAME_PLURAL,
  tableName: VALUATIONS_SPREADSHEET_TITLE,
  tableSlug: VALUATIONS_SPREADSHEET_TABLE,
});

// Approaches

export const VALUATIONS_BACKSOLVE_APPROACH = 'Backsolve';
export const VALUATIONS_BACKSOLVE_APPROACH_MENU = 'Backsolve';
export const VALUATIONS_DISCOUNT_CASH_FLOW_APPROACH = 'Discounted Cash Flow';
export const VALUATIONS_EXTERNAL_VALUATION_APPROACH = 'External Valuation';
export const VALUATIONS_FUTURE_EXIT_APPROACH = 'Future Exit';
export const VALUATIONS_PRIVATE_TRANSACTIONS_APPROACH = 'Private Transactions';
export const VALUATIONS_PUBLIC_COMPANIES_APPROACH = 'Public Comps';
export const VALUATIONS_PUBLIC_COMPANIES_APPROACH_MENU = 'Public Comps';
export const VALUATIONS_PUBLIC_TRANSACTIONS_APPROACH = 'Transaction Comps';
export const VALUATIONS_PUBLIC_TRANSACTIONS_APPROACH_MENU = 'Transaction Comps';
export const VALUATIONS_SPECIFIED_SHARE_VALUES_APPROACH = 'Specified Share Values';
export const VALUATIONS_SPECIFIED_SHARE_VALUES_APPROACH_MENU = 'Specified Share Values';
export const SET_AS_CALIBRATION = 'Set as Calibration';
export const CALIBRATION_MODEL_NAME = 'Calibration';
export const INDEX_AFTER_ALLOCATION_SCENARIO_METHODS = 3;
export const USE_MULTIPLE_PREMIUM_DISCOUNT = 'Enable Specified Multiple Premium/(Discount)';
export const USE_FORWARD_MULTIPLE_DISCOUNT = 'Enable Forward Multiple Discount';

export const VALUATIONS_APPROACH_TYPES = Object.freeze([
  VALUATIONS_BACKSOLVE_APPROACH,
  VALUATIONS_DISCOUNT_CASH_FLOW_APPROACH,
  VALUATIONS_EXTERNAL_VALUATION_APPROACH,
  VALUATIONS_FUTURE_EXIT_APPROACH,
  VALUATIONS_PUBLIC_COMPANIES_APPROACH,
  VALUATIONS_PUBLIC_TRANSACTIONS_APPROACH,
  VALUATIONS_SPECIFIED_SHARE_VALUES_APPROACH,
  CALIBRATION_MODEL_NAME,
]);

// Approaches Properties

export const VALUATIONS_APPROACH_BACKSOLVE_PROPERTY = 'valuations_approach_backsolve';
export const VALUATIONS_APPROACH_DCF_PROPERTY = 'valuations_approach_dcf';
export const VALUATIONS_APPROACH_EV_PROPERTY = 'valuations_approach_ev';
export const VALUATIONS_APPROACH_FUTURE_EXIT_PROPERTY = 'valuations_approach_future_exit';
export const VALUATIONS_APPROACH_GPC_PROPERTY = 'valuations_approach_gpc';
export const VALUATIONS_APPROACH_GPT_PROPERTY = 'valuations_approach_gpt';
export const VALUATIONS_APPROACH_SSV_PROPERTY = 'valuations_approach_ssv';
export const VALUATIONS_APPROACH_GPC_REFERENCE = 'gpc_reference';
export const VALUATIONS_APPROACH_GPT_REFERENCE = 'gpt_reference';

export const VALUATIONS_APPROACH_TYPES_PROPERTIES_MAP = new Map([
  [VALUATIONS_BACKSOLVE_APPROACH, VALUATIONS_APPROACH_BACKSOLVE_PROPERTY],
  [VALUATIONS_DISCOUNT_CASH_FLOW_APPROACH, VALUATIONS_APPROACH_DCF_PROPERTY],
  [VALUATIONS_EXTERNAL_VALUATION_APPROACH, VALUATIONS_APPROACH_EV_PROPERTY],
  [VALUATIONS_FUTURE_EXIT_APPROACH, VALUATIONS_APPROACH_FUTURE_EXIT_PROPERTY],
  [VALUATIONS_PUBLIC_COMPANIES_APPROACH, VALUATIONS_APPROACH_GPC_PROPERTY],
  [VALUATIONS_PUBLIC_TRANSACTIONS_APPROACH, VALUATIONS_APPROACH_GPT_PROPERTY],
  [VALUATIONS_SPECIFIED_SHARE_VALUES_APPROACH, VALUATIONS_APPROACH_SSV_PROPERTY],
]);

// Guideline Public Transactions

export const VALUATIONS_MA_COMPS = 'Transaction Comps';
export const VALUATIONS_LINK_WITH_GPT = 'link_with_gpt';
export const VALUATIONS_VALUATION_APPROACH_GPT = 'valuation_approach_gpt';

// Guideline Public Companies

export const VALUATIONS_CANNOT_DELETE_GPC = 'This approach is being used in a Market Adjustment and/or Future Exit';
export const VALUATIONS_GPC_COMPS_NEED_REFRESH =
  'Refresh Data required before this Public Comps approach can be changed to Equity Value.';
export const VALUATIONS_LINK_WITH_GPC = 'link_with_gpc';
export const VALUATIONS_SPECIFIED_ID = 0;
export const VALUATIONS_SPECIFIED_LABEL = 'Specified';
export const VALUATIONS_USE_CAP_IQ_ID =
  'A Company ID from Capital IQ can be used instead of the stock ticker to retrieve data on acquired companies or other entities that are not available via the ticker. The Capital IQ Company ID uses the format ';
export const VALUATIONS_VALUATION_APPROACH_GPC = 'valuation_approach_gpc';
export const VALUATIONS_WAS_NOT_FOUND_MD = 'was not found for the current measurement date';
export const ENTERPRISE_VALUE_OPTION = 'Enterprise Value';
export const EQUITY_VALUE_OPTION = 'Equity Value';
export const ENTERPRISE_EQUITY_OPTIONS = [ENTERPRISE_VALUE_OPTION, EQUITY_VALUE_OPTION];
export const TANGIBLE_BOOK_VALUE: keyof GPC = 'tangible_book_value';
export const TANGIBLE_BOOK_VALUE_ESTIMATE: keyof GPC = 'tangible_book_value_estimate';
export const NET_INCOME: keyof GPC = 'net_income';
export const NET_INCOME_TO_COMMON_EXCL_EXTRA_ITEMS: keyof GPC = 'net_income_to_common_excl_extra_items';
export const FORWARD_NET_INCOME: keyof GPC = 'forward_net_income';

export const EQUITY_ATTRIBUTES_NEEDED: (keyof GPC)[] = [
  TANGIBLE_BOOK_VALUE,
  TANGIBLE_BOOK_VALUE_ESTIMATE,
  NET_INCOME,
  NET_INCOME_TO_COMMON_EXCL_EXTRA_ITEMS,
  FORWARD_NET_INCOME,
];

// Benchmark

export const VALUATIONS_BENCHMARK_APPROACH_TYPES = [
  VALUATIONS_PUBLIC_TRANSACTIONS_APPROACH,
  VALUATIONS_PUBLIC_COMPANIES_APPROACH,
];

export const VALUATIONS_BENCHMARK_RELATED_APPROACHES_TYPES = new Map([
  [VALUATIONS_PUBLIC_COMPANIES_APPROACH, VALUATIONS_APPROACH_GPC_PROPERTY],
  [VALUATIONS_PUBLIC_TRANSACTIONS_APPROACH, VALUATIONS_APPROACH_GPT_PROPERTY],
  [VALUATIONS_DISCOUNT_CASH_FLOW_APPROACH, VALUATIONS_APPROACH_DCF_PROPERTY],
]);

// Cap Table

export const VALUATIONS_CAP_TABLE_CURRENCY_APPROACHES = [
  VALUATIONS_BACKSOLVE_APPROACH,
  VALUATIONS_SPECIFIED_SHARE_VALUES_APPROACH,
];

// References

export const VALUATIONS_VALUATION_REFERENCES_MAP = new Map([
  [VALUATIONS_EXTERNAL_VALUATION_APPROACH, EXTERNAL_VALUATION_REFERENCE_TYPE],
  [VALUATIONS_SPECIFIED_SHARE_VALUES_APPROACH, SSV_REFERENCE_TYPE],
]);

// Keys

export const VALUATIONS_SPREADSHEET_ENTERPRISE_VALUE_KEY = 'enterprise_value';
export const VALUATIONS_SPREADSHEET_EQUITY_VALUE_KEY = 'equity_value';
export const VALUATIONS_SPREADSHEET_GPC_KEY = 'gpc';
export const VALUATIONS_SPREADSHEET_OTHER_KEY = 'compsSheet';

// Errors

export const VALUATIONS_APPROACH_TYPE_NOT_IMPLEMENTED_MESSAGE = 'Approach type not implemented';
export const VALUATIONS_MISSING_FINANCIAL_STATEMENT_TITLE = 'There is no financial statement for this measurement date';
export const VALUATIONS_MISSING_FINANCIAL_STATEMENT_TAGLINE = 'Please save a financial statement to continue';
export const VALUATIONS_FINAL_VALUATION_LOCKED_MESSAGE =
  'This Valuation table version is used in a final valuation, any changes saved could affect that valuation';

// Regex

export const VALUATIONS_SPREADSHEET_APPROACH_TABLE_NAME_REGEX = /\b([a-zA-Z]+_\d+)/;

// Buttons and labels
export const REMOVE_READY_FOR_AUDIT = 'Remove Ready for Audit';
export const SET_AS_READY_FOR_AUDIT = 'Set as Ready for Audit';
export const READY_FOR_AUDIT = 'Ready for Audit';
export const CONFIRMATION_MESSAGE_READY_FOR_AUDIT = 'Are you sure you want to mark this valuation as ready for audit?';
export const CONFIRMATION_MESSAGE_REMOVE_READY_FOR_AUDIT =
  'Are you sure you want to remove the ready for audit status from this valuation?';
export const INVALID_CHARACTERS_NAMES = ['\''];
export const INVALID_CHARACTERS_REGEX = new RegExp(`[${INVALID_CHARACTERS_NAMES.join('')}]`);

// Valuation Approaches

export const DEFAULT_VALUES = {
  name: moment().format('MM/DD/YYYY'),
};

export const DEFAULT_VERSION_FORM_STATE: NewVersionFormState = {
  dbErrors: {},
  errors: {},
  isValid: false,
  touched: {},
  values: {
    ...DEFAULT_VALUES,
  },
};

export const ALLOCATION_SCENARIO_DEFAULT_VALUES = {
  approach_uuid: null,
  deleted_at: null,
  dilution_percentage: '0',
  discount_rate: null,
  equity_value: '0',
  exit_date: null,
  exit_equity_value: '0',
  id: 0,
  is_deleted: false,
  maturity: '0',
  name: 'Empty Scenario',
  order: 0,
  scenario_method: ALLOCATION_SCENARIO_METHOD_OPM,
  scenario_type: ALLOCATION_SCENARIO_TYPE_CURRENT_VALUE,
  scenario_values: {
    aggregate_values: [],
    future_values: [],
    present_values: [],
  },
  total_aggregate_value: null,
};

// Titles
export const CHANGE_TO_SCALAR_VALUATION_LABEL = 'Change to Scalar Valuation';
export const CHANGE_VALUATION_OWNERSHIP_LABEL = 'Change Valuation Ownership';
export const DELETE_VALUATION_BUTTON_LABEL = 'Delete';
export const EDIT_APPROACH_NAME_LABEL = 'Edit Approach';
export const EDIT_CALIBRATION_NAME_LABEL = 'Edit Calibration';
export const GIVE_OWNERSHIP_TO_FIRM_LABEL = 'Give Ownership to Firm';
export const MAKE_FINAL_ALLOCATION_BUTTON_LABEL = 'Make Final';
export const MAKE_FINAL_ALLOCATION_CONFIRMATION_MESSAGE = 'Are you sure you want to Make this Allocation Final?';
export const PUBLISH_ALLOCATION_BUTTON_LABEL = 'Publish';
export const PUBLISH_ALLOCATION_CONFIRMATION_MESSAGE = 'Are you sure you want to Publish this Allocation?';
export const SAVE_AS_NEW_VERSION_LABEL = 'Save as New Version';
export const SAVE_VALUATION_BUTTON_LABEL = 'Save';
export const SCALAR_OWNED_DRAFT_MESSAGE = 'This Valuation will be viewable once Scalar publishes this version';
export const NOT_READY_FOR_AUDIT_MESSAGE = 'This Valuation is not ready for audit';
export const CHANGE_OWNERSHIP_LABEL = 'Change ownership';

// Approach Menu Options
export const CHANGE_FINANCIAL_STATEMENT_LABEL = 'Change Financial Statement';
export const DELETE_APPROACH_LABEL = 'Delete Approach';
export const DELETE_CALIBRATION_LABEL = 'Delete Calibration';
export const ENTERPRISE_VALUE_LABEL = 'Enterprise Value';
export const EQUITY_VALUE_LABEL = 'Equity Value';
export const FINANCIAL_STATEMENT_LABEL = 'Financial Statement';

// Delays
export const LOADING_TIMEOUT_DELAY = 500;
export const REDIRECT_TIMEOUT_DELAY = 100;
